/**
 * Rol / alt rol rozeti — etiket ve renk çözümü.
 *
 * NEDEN ORTAK: Kullanıcının `subRole` değeri artık firmanın **rol şablonu
 * adıdır** (bkz. server → role-template.service.js). Yani sabit bir sözlükle
 * eşleşmeyebilir: bir firma "Saha Sorumlusu" adında şablon açabilir. Eski
 * haritalar İngilizce anahtarlıydı ("playmaker", "management"), şablon adları
 * Türkçeye dönünce hiçbiri eşleşmedi ve tüm rozetler gri kaldı.
 *
 * Çözüm iki katmanlı:
 *   1. Bilinen adlar (varsayılan şablonlar + eski canonical değerler) sabit
 *      renk alır — Plasiyer her firmada aynı yeşil olur.
 *   2. Bilinmeyen ad, adından türetilen deterministik bir renk alır. Aynı ad
 *      her yerde aynı rengi verir; farklı adlar birbirinden ayrışır.
 *
 * Renkler `tailwind.config.js` dışındaki standart Tailwind aileleridir
 * (hardcoded hex yok — bkz. docs/rules/styling.md).
 */

/** Aksan/büyük-küçük farkını siler: "Yonetici" ile "Yönetici" aynı anahtar olur. */
const normalize = (value) =>
  (value || "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g")
    .replace(/\s+/g, " ");

/** Ana rol (discriminator) etiketleri — subRole yoksa buna düşülür. */
const ROLE_LABELS = {
  admin: "Sistem Yöneticisi",
  storage: "Depo",
  dealer: "Bayi",
  customer: "Müşteri",
  enduser: "Son Kullanıcı",
  machineworker: "Makine İşçisi",
};

/**
 * Bilinen adlar → Tailwind renk ailesi. Anahtarlar normalize edilmiş hâldedir;
 * hem varsayılan şablon adları hem de eski canonical değerler bulunur ki
 * migrate olmamış kayıtlar da doğru renklensin.
 */
const KNOWN_FAMILY = {
  // Varsayılan şablonlar
  yonetici: "blue",
  yonetim: "blue",
  management: "blue",
  plasiyer: "emerald",
  playmaker: "emerald",
  servis: "indigo",
  service: "indigo",
  muhasebe: "amber",
  accounting: "amber",
  depo: "sky",
  // Diğer bilinen roller
  "sistem yoneticisi": "purple",
  admin: "purple",
  gelistirici: "cyan",
  developer: "cyan",
  uretim: "orange",
  manufacturer: "orange",
  bayi: "violet",
  dealer: "violet",
  musteri: "teal",
  customer: "teal",
  "son kullanici": "slate",
  enduser: "slate",
  "makine iscisi": "green",
  machineworker: "green",
};

/** Özel adlı şablonların dağıtıldığı palet — bilinen renklerle çakışmaz. */
const FALLBACK_FAMILIES = [
  "rose",
  "fuchsia",
  "lime",
  "stone",
  "pink",
  "yellow",
  "red",
  "zinc",
  "neutral",
];

/**
 * Tailwind JIT sınıf adlarını string birleştirmeyle üretemez (tarama yapamaz),
 * bu yüzden tüm kombinasyonlar burada LİTERAL olarak durur.
 */
const FAMILY_CLASS = {
  blue: "bg-blue-50 text-blue-700 border-blue-100",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  sky: "bg-sky-50 text-sky-700 border-sky-100",
  purple: "bg-purple-50 text-purple-700 border-purple-100",
  cyan: "bg-cyan-50 text-cyan-700 border-cyan-100",
  orange: "bg-orange-50 text-orange-700 border-orange-100",
  violet: "bg-violet-50 text-violet-700 border-violet-100",
  teal: "bg-teal-50 text-teal-700 border-teal-100",
  slate: "bg-slate-50 text-slate-700 border-slate-100",
  green: "bg-green-50 text-green-700 border-green-100",
  rose: "bg-rose-50 text-rose-700 border-rose-100",
  fuchsia: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100",
  lime: "bg-lime-50 text-lime-700 border-lime-100",
  stone: "bg-stone-50 text-stone-700 border-stone-100",
  pink: "bg-pink-50 text-pink-700 border-pink-100",
  yellow: "bg-yellow-50 text-yellow-700 border-yellow-100",
  red: "bg-red-50 text-red-700 border-red-100",
  zinc: "bg-zinc-50 text-zinc-700 border-zinc-100",
  neutral: "bg-neutral-50 text-neutral-700 border-neutral-100",
  gray: "bg-gray-50 text-gray-700 border-gray-100",
};

/** Ad → palet indeksi. Deterministik: aynı ad her zaman aynı rengi verir. */
const hashToIndex = (value, size) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 100000;
  }
  return hash % size;
};

/**
 * Rozet metni. `subRole` zaten görünen addır (şablon adı), olduğu gibi kullanılır;
 * yoksa ana rolün etiketine düşülür.
 */
export const getRoleLabel = (role, subRole) => {
  // Son kullanıcının alt rolü hep "enduser" — rozet her zaman Türkçe (#201).
  if (role === "enduser") return ROLE_LABELS.enduser;
  const key = normalize(subRole);
  if (key) {
    // Alt rol kullanıcının KENDİ ana rol adıysa ("customer"/"customer") ana
    // rolün Türkçesi. Başka rol adlarına dokunulmaz — müşterinin varsayılan
    // alt rolü "Admin" "Sistem Yöneticisi"ne dönmesin.
    if (key === normalize(role) && ROLE_LABELS[role]) return ROLE_LABELS[role];
    // Eski canonical değerler ("playmaker" vb.) hâlâ İngilizce görünmesin
    const legacy = {
      management: "Yönetim",
      playmaker: "Plasiyer",
      service: "Servis",
      accounting: "Muhasebe",
      developer: "Geliştirici",
      manufacturer: "Üretim",
      depo: "Depo",
    };
    return legacy[key] || subRole;
  }
  return ROLE_LABELS[role] || role || "Kullanıcı";
};

/** Rozet renk sınıfları (bg + text + border). */
export const getRoleBadgeClass = (role, subRole) => {
  const key = normalize(subRole) || normalize(role);
  if (!key) return FAMILY_CLASS.gray;
  const family =
    KNOWN_FAMILY[key] ||
    FALLBACK_FAMILIES[hashToIndex(key, FALLBACK_FAMILIES.length)];
  return FAMILY_CLASS[family] || FAMILY_CLASS.gray;
};

const roleBadge = { getRoleLabel, getRoleBadgeClass };

export default roleBadge;
